import React, { useState, useEffect } from 'react';
import { 
  Table, Card, Typography, Input, Select, Button, Row, Col, 
  Statistic, Divider, Space, Tag, Alert, Spin, Modal 
} from 'antd';
import { SearchOutlined, ReloadOutlined, ExportOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Title, Text } = Typography;
const { Option } = Select;

const StudentReport = () => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({ total: 0, passed: 0, passRate: 0 });
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [filters, setFilters] = useState({
    school: '',
    className: [],
    name: '',
    passStatus: ''
  });
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [examHistoryVisible, setExamHistoryVisible] = useState(false);
  const [rawUsers, setRawUsers] = useState([]);
  const [rawExams, setRawExams] = useState([]);

  // 获取学校列表
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const res = await axios.get('/api/reports/schools');
        setSchools(res.data);
      } catch (err) {
        console.error('获取学校列表失败:', err);
      }
    };

    fetchSchools();
  }, []);

  // 获取班级列表
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const params = filters.school ? { school: filters.school } : {};
        const res = await axios.get('/api/reports/classes', { params });
        setClasses(res.data);
      } catch (err) {
        console.error('获取班级列表失败:', err);
      }
    };

    fetchClasses();
  }, [filters.school]);

  // 获取学生数据
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await axios.get('/api/reports/students', { params: filters });
      setStudents(res.data.students);
      setStats(res.data.stats);
      
      // 保存调试数据
      if (res.data.debug) {
        setRawUsers(res.data.debug.rawUsers);
        setRawExams(res.data.debug.rawExams);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('获取学生数据失败:', err);
      setError('获取学生数据失败，请稍后重试。');
      setLoading(false);
    }
  };

  const fetchDebugData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 获取原始用户数据
      const usersRes = await axios.get('/api/reports/users');
      setRawUsers(usersRes.data);
      
      // 获取原始考试数据
      const examsRes = await axios.get('/api/reports/exams');
      setRawExams(examsRes.data);
      
      setLoading(false);
    } catch (err) {
      console.error('获取调试数据失败:', err);
      setError('获取调试数据失败: ' + err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // 处理筛选变化
  const handleFilterChange = (name, value) => {
    // 切换学校时清空已选班级（多选班级限定在同一学校内）
    if (name === 'school') {
      setFilters({ ...filters, school: value, className: [] });
    } else {
      setFilters({ ...filters, [name]: value });
    }
  };

  // 应用筛选
  const applyFilters = () => {
    fetchStudents();
  };

  // 重置筛选
  const resetFilters = () => {
    setFilters({
      school: '',
      className: [],
      name: '',
      passStatus: ''
    });
    setTimeout(fetchStudents, 0);
  };

  // 导出Excel
  const exportToExcel = () => {
    const getBestTotalScore = (examHistory = []) => {
      if (!Array.isArray(examHistory) || examHistory.length === 0) return null;

      const withScore = examHistory
        .filter(exam => exam && typeof exam.totalScore === 'number' && !Number.isNaN(exam.totalScore));

      if (withScore.length === 0) return null;

      // 有“通过”记录时，只在通过记录里取最高分；否则取所有考试最高分
      const passed = withScore.filter(exam => exam.status === '通过');
      const candidates = passed.length > 0 ? passed : withScore;

      return Math.max(...candidates.map(exam => exam.totalScore));
    };

    const getFirstPassDateText = (examHistory = []) => {
      if (!Array.isArray(examHistory) || examHistory.length === 0) return '-';

      const passed = examHistory.filter(exam => exam && exam.status === '通过' && exam.createdAt);
      if (passed.length === 0) return '-';

      const earliest = passed.reduce((min, cur) => {
        const minTime = new Date(min.createdAt).getTime();
        const curTime = new Date(cur.createdAt).getTime();
        return curTime < minTime ? cur : min;
      });

      return new Date(earliest.createdAt).toLocaleDateString();
    };

    // 准备数据
    const exportData = students.map(student => ({
      '姓名': student.name,
      '学校': student.school,
      '班级': student.className,
      '电话': student.phone,
      '考试次数': student.examsTaken,
      '考试状态': student.hasPassed ? '已通过' : '未通过',
      '最好成绩(总分)': (() => {
        const best = getBestTotalScore(student.examHistory);
        return best === null ? '-' : best;
      })(),
      '通过日期': getFirstPassDateText(student.examHistory)
    }));
    
    // 创建工作表
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // 创建工作簿并添加工作表
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '学生考试情况');
    
    // 生成Excel文件
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // 保存文件
    const fileName = `学生考试情况报表_${new Date().toLocaleDateString()}.xlsx`;
    saveAs(data, fileName);
  };

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '学校',
      dataIndex: 'school',
      key: 'school',
      width: 180,
    },
    {
      title: '班级',
      dataIndex: 'className',
      key: 'className',
      width: 180,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '考试次数',
      dataIndex: 'examsTaken',
      key: 'examsTaken',
      width: 100,
      sorter: (a, b) => a.examsTaken - b.examsTaken,
    },
    {
      title: '考试状态',
      key: 'status',
      width: 120,
      render: (text, record) => (
        <Tag color={record.hasPassed ? 'green' : 'red'}>
          {record.hasPassed ? '已通过' : '未通过'}
        </Tag>
      ),
      filters: [
        { text: '已通过', value: 'passed' },
        { text: '未通过', value: 'failed' },
      ],
      onFilter: (value, record) => {
        if (value === 'passed') {
          return record.hasPassed;
        }
        return !record.hasPassed;
      },
    },
    {
      title: '通过日期',
      key: 'passDate',
      width: 150,
      render: (text, record) => {
        if (record.examHistory) {
          // 找到第一个通过的考试
          const passedExam = record.examHistory.find(exam => exam.status === '通过');
          if (passedExam) {
            const date = new Date(passedExam.createdAt);
            return date.toLocaleDateString();
          }
        }
        return '-';
      },
    },
    {
      title: '最新考试',
      key: 'latestExam',
      width: 180,
      render: (text, record) => {
        if (record.latestExam) {
          const exam = record.latestExam;
          return (
            <span>
              {exam.professionalCategory}: {exam.totalScore}分
              <Tag 
                color={exam.status === '通过' ? 'green' : 'red'} 
                style={{ marginLeft: 8 }}
              >
                {exam.status}
              </Tag>
            </span>
          );
        }
        return '-';
      },
    },
  ];

  const showExamHistory = (student) => {
    setSelectedStudent(student);
    setExamHistoryVisible(true);
  };

  const closeExamHistory = () => {
    setExamHistoryVisible(false);
    setSelectedStudent(null);
  };

  return (
    <Card>
      <Title level={2}>学生考试通过情况</Title>
      <Divider />
      
      {error && (
        <Alert
          message="错误"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}
      
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Statistic 
            title="总学生数" 
            value={stats.total} 
            suffix="人"
          />
        </Col>
        <Col span={8}>
          <Statistic 
            title="通过人数" 
            value={stats.passed} 
            suffix="人"
            valueStyle={{ color: '#3f8600' }}
          />
        </Col>
        <Col span={8}>
          <Statistic 
            title="通过率" 
            value={stats.passRate} 
            suffix="%" 
            precision={2}
            valueStyle={{ color: stats.passRate > 60 ? '#3f8600' : '#cf1322' }}
          />
        </Col>
      </Row>
      
      <Divider />
      
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Select
            placeholder="选择学校"
            style={{ width: '100%' }}
            allowClear
            value={filters.school}
            onChange={(value) => handleFilterChange('school', value)}
          >
            {schools.map(school => (
              <Option key={school} value={school}>{school}</Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Select
            mode="multiple"
            placeholder={filters.school ? '选择班级(可多选)' : '请先选择学校'}
            style={{ width: '100%' }}
            allowClear
            disabled={!filters.school}
            value={filters.className}
            onChange={(value) => handleFilterChange('className', value)}
            maxTagCount="responsive"
            optionFilterProp="children"
          >
            {classes.map(cls => (
              <Option key={cls} value={cls}>{cls}</Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Input
            placeholder="学生姓名"
            value={filters.name}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            allowClear
          />
        </Col>
        <Col span={6}>
          <Select
            placeholder="考试状态"
            style={{ width: '100%' }}
            allowClear
            value={filters.passStatus}
            onChange={(value) => handleFilterChange('passStatus', value)}
          >
            <Option value="passed">已通过</Option>
            <Option value="failed">未通过</Option>
          </Select>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Space>
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              onClick={applyFilters}
            >
              筛选
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={resetFilters}
            >
              重置
            </Button>
            <Button 
              type="primary" 
              icon={<ExportOutlined />}
              onClick={exportToExcel}
            >
              导出Excel
            </Button>
          </Space>
        </Col>
      </Row>
      
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={students}
          rowKey="_id"
          pagination={{ 
            defaultPageSize: 10, 
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条数据`
          }}
          onRow={(record) => ({
            onClick: () => showExamHistory(record),
          })}
          scroll={{ x: 1100 }}
        />
      </Spin>

      {selectedStudent && (
        <Modal
          title={`${selectedStudent.name} 的考试记录`}
          visible={examHistoryVisible}
          onCancel={closeExamHistory}
          footer={[
            <Button key="close" onClick={closeExamHistory}>
              关闭
            </Button>
          ]}
          width={800}
        >
          <Table
            dataSource={selectedStudent.examHistory || []}
            rowKey="_id"
            columns={[
              {
                title: '考试时间',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: text => new Date(text).toLocaleString(),
              },
              {
                title: '专业类别',
                dataIndex: 'professionalCategory',
                key: 'professionalCategory',
              },
              {
                title: '专业成绩',
                dataIndex: 'professionalScore',
                key: 'professionalScore',
              },
              {
                title: '消防安全成绩',
                dataIndex: 'fireSafetyScore',
                key: 'fireSafetyScore',
              },
              {
                title: '总分',
                dataIndex: 'totalScore',
                key: 'totalScore',
              },
              {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                render: text => (
                  <Tag color={text === '通过' ? 'green' : 'red'}>
                    {text}
                  </Tag>
                ),
              }
            ]}
          />
        </Modal>
      )}
    </Card>
  );
};

export default StudentReport; 